import {
  QueryClient,
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { FinancialModelAuditSource } from "./layers/financialModelAuditTypes";
import { chunk, cloneDeep, isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  LOB,
  UpdateAddresses,
  UpdateReport,
  addComment,
  addManualAddress,
  deleteComment,
  editComment,
  calculatePremium,
  createCRMContact,
  deleteAdditionalFile,
  deleteAttachment,
  bulkEnrichSubmissions,
  deleteEmailThread,
  deleteReport,
  enqueueEqbdRater,
  enqueueRater,
  fetchEmailThreads,
  getBatchComments,
  getClearanceEmailRecipients,
  generatePolicyFlyApplication,
  updatePolicyFlyApplication,
  linkPolicyFlyApplication,
  unlinkPolicyFlyApplication,
  generatePolicyNumbers,
  LocationClassification,
  LocationClassificationKey,
  LocationKey,
  getDeductibleSummary,
  getLocationClassifications,
  getGoogleGroupStats,
  getIntegrationData,
  getLimitSummary,
  getLayerGateExposure,
  getFinancialModelAudit,
  getGateTrackingBase,
  getPortfolioGateAccumulation,
  getAalPremiumGatePortfolio,
  getAalPremiumGateTracking,
  getUpdatingReports,
  listAdditionalFiles,
  listCRMContacts,
  listPolicyFlyApplications,
  listReport,
  listReports,
  listUsers,
  loadAddressesForMap,
  precheck,
  loadAddresses,
  fetchFreshness,
  updateAddresses,
  updateCRMContact,
  extractStagedAttachments,
  updateReport,
  uploadAdditionalFile,
  uploadEmailThread,
} from "../../api/routes";
import * as client from "../../client";
import { useClientId } from "../../ClientConfig";
import { useBoundedQueries } from "../../util";
import {
  applyOverridesToMetadata,
  applyOverridesToMetadataReponse,
  SafeReportMetadata,
  SafeReportResponse,
} from "./Conversion";
import { PerilDataSource } from "./layers/util";
import {
  addressesForMapKeys,
  commentKeys,
  crmContactKeys,
  freshnessKeys,
  bookKeys,
  googleGroupStatsKeys,
  jobKeys,
  policyFlyKeys,
  reportKeys,
  updatingReportKeys,
  userKeys,
  windTierKeys,
} from "./queryKeys";

// Invalidate everything cached under a report's detail prefix (the report
// itself, premiums, prechecks, summaries, files, threads, ...) except the
// exact keys the caller just wrote via setQueryData — invalidating a patched
// key would kick off a refetch that races with, and then overwrites, the
// patch.
// Book-wide summaries (gate accumulation) aggregate every bound/quoted deal, so
// any report change can shift them — drop them whenever a report mutates or a
// freshness poll reports a change.
const invalidateBookSummaries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: bookKeys.all });
};

const invalidateReportDetail = (
  queryClient: QueryClient,
  reportId: string,
  exceptPatched: ReadonlyArray<readonly unknown[]>
): void => {
  queryClient.invalidateQueries({
    queryKey: reportKeys.detail(reportId),
    predicate: (query) =>
      !exceptPatched.some((key) => isEqual(query.queryKey, key)),
  });
  invalidateBookSummaries(queryClient);
};

/**
 * Reconcile the report detail cache from an update/reorder response. Shared by
 * useUpdateReport and useReorderPolicyField so the two — which write the same
 * keys — can't drift.
 *
 * The response omits SOV addresses when `use_old_sovs` is set (they're large and
 * unchanged), so we splice the cached SOVs back in; if we somehow have no cached
 * report to splice from, we refetch rather than store the stripped one.
 *
 * `invalidateDerived` triggers the broad refresh of list views and everything
 * derived from the report (TIV, premiums, summaries, ...). A content edit needs
 * it; a sort-only reorder does not (only display order changed).
 */
const reconcileReportDetail = (
  queryClient: QueryClient,
  reportId: string,
  result: client.UpdateReportResponse,
  { invalidateDerived }: { invalidateDerived: boolean }
): void => {
  const key = reportKeys.detail(reportId);
  if (invalidateDerived) {
    queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
    invalidateReportDetail(queryClient, reportId, [key]);
    queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
  }

  const old = queryClient.getQueryData<client.ReportResponse>(key);
  const next = result.report;
  if (result.use_old_sovs && old) {
    queryClient.setQueryData<client.ReportResponse>(key, {
      ...next,
      report_json: { ...next.report_json, sovs: old.report_json.sovs },
    });
  } else if (!result.use_old_sovs) {
    queryClient.setQueryData(key, next);
  } else {
    // use_old_sovs but nothing cached to splice from — refetch rather than
    // persist the SOV-stripped response.
    queryClient.invalidateQueries({ queryKey: key });
  }
};

const useReportsInner = (lobs: LOB[]) => {
  // Subscribe to cross-device changes whenever a report list is shown; the
  // freshness poll, not a blind interval, drives list refreshes.
  useReportsFreshness(lobs);
  return useQuery({
    enabled: lobs.length > 0,
    queryKey: reportKeys.list(lobs),
    queryFn: () => listReports(lobs),
  });
};

export const useReports = (lobs: LOB[]) => useReportsInner(lobs);
export const useSafeReports = (lobs: LOB[]) => {
  const clientId = useClientId();
  const reports = useReportsInner(lobs);
  return useMemo(
    () => ({
      ...reports,
      data: reports.data
        ? reports.data.map((report) =>
            applyOverridesToMetadataReponse(report, clientId)
          )
        : reports.data,
    }),
    [reports.data]
  );
};

// Dashboard cross-device sync: poll the latest change across the user's reports
// in `lobs` and refresh only what changed. We echo back the server's last
// timestamp (the prev poll's response) rather than deriving it from cached data,
// which excludes deleted rows and so could never catch a deletion.
const DASHBOARD_FRESHNESS_POLL_MS = 10_000;

const useReportsFreshness = (lobs: LOB[]): void => {
  const queryClient = useQueryClient();
  useQuery({
    queryKey: freshnessKeys.reports(),
    enabled: lobs.length > 0,
    refetchInterval: DASHBOARD_FRESHNESS_POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<client.FreshnessResponse> => {
      const prev = queryClient.getQueryData<client.FreshnessResponse>(
        freshnessKeys.reports()
      );

      const result = await fetchFreshness({
        reportId: null,
        clientModifiedAtTs: prev?.modified_at_ts ?? null,
        clientCommentsModifiedAtTs: prev?.comments_modified_at_ts ?? null,
      });

      // `prev` guards the first poll (data loads with the page); skip while a
      // save is in flight, as that mutation already invalidates its caches.
      const idle = queryClient.isMutating() === 0;

      // lists() prefix, not list(lobs): the dashboard holds list queries keyed by
      // different lob subsets, any of which a change can affect.
      if (prev && result.needs_refresh && idle) {
        queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
        invalidateBookSummaries(queryClient);
      }
      if (prev && result.comments_need_refresh && idle) {
        queryClient.invalidateQueries({ queryKey: commentKeys.all });
      }

      return result;
    },
  });
};

export const usePolicyFlyApplications = (statuses: string) => {
  const clientId = useClientId();
  return useQuery({
    queryKey: policyFlyKeys.list(statuses),
    queryFn: async (): Promise<SafePolicyFlyApplicationRow[]> => {
      const raw = await listPolicyFlyApplications(statuses);
      return raw.map((app) => ({
        ...app,
        report_metadata: applyOverridesToMetadata(
          app.report_metadata,
          clientId
        ),
      }));
    },
    refetchInterval: 120_000,
  });
};
export type SafePolicyFlyApplicationRow = Omit<
  client.PolicyFlyApplicationRow,
  "report_metadata"
> & { report_metadata: SafeReportMetadata };

export const useReport = (reportId?: string) => {
  // Subscribe to cross-device changes whenever a report is open.
  useReportFreshness(reportId);
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: reportKeys.detail(reportId ?? ""),
    queryFn: async (): Promise<client.ReportResponse | undefined> => {
      if (!reportId) return undefined;

      const report = await listReport(reportId, false);

      const addressesBySov = await Promise.all(
        report.report_json.sovs.map(async (sov) => {
          const { addresses } = await loadAddresses(reportId, sov.sov_id);
          const live = addresses.filter((a) => !a.is_deleted);
          live.sort((a, b) =>
            (a.default_sort_key ?? "").localeCompare(b.default_sort_key ?? "")
          );
          return [sov.sov_id, live] as const;
        })
      );

      const bySovId = Object.fromEntries(addressesBySov);
      return {
        ...report,
        report_json: {
          ...report.report_json,
          sovs: report.report_json.sovs.map((sov) => ({
            ...sov,
            addresses: bySovId[sov.sov_id] ?? [],
          })),
        },
      };
    },
  });
};

// Cross-device sync: while a report is open, poll the server for changes and
// invalidate the affected caches. Report data and comments are independent
// dimensions so a remote comment doesn't refetch the heavy report+addresses
// payload. For comments we echo back the server's last-reported timestamp
// rather than deriving it from cached comments, which exclude soft-deleted rows
// and so could never catch a deletion.
const REPORT_FRESHNESS_POLL_MS = 5_000;

const useReportFreshness = (reportId?: string): void => {
  const queryClient = useQueryClient();
  useQuery({
    queryKey: freshnessKeys.report(reportId ?? ""),
    enabled: !!reportId,
    refetchInterval: REPORT_FRESHNESS_POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<client.FreshnessResponse | null> => {
      if (!reportId) return null;

      const cached = queryClient.getQueryData<client.ReportResponse>(
        reportKeys.detail(reportId)
      );
      // Nothing to compare against yet (initial load in flight); try next tick.
      if (!cached) return null;

      const prev = queryClient.getQueryData<client.FreshnessResponse>(
        freshnessKeys.report(reportId)
      );

      const result = await fetchFreshness({
        reportId,
        clientModifiedAtTs: cached.modified_at_ts,
        clientCommentsModifiedAtTs: prev?.comments_modified_at_ts ?? null,
      });

      // Skip while a save is in flight; the mutation updates its own cache.
      const idle = queryClient.isMutating() === 0;

      if (result.needs_refresh && idle) {
        queryClient.invalidateQueries({
          queryKey: reportKeys.detail(reportId),
        });
        invalidateBookSummaries(queryClient);
      }

      // `prev` guards the first poll (comments load with the page). commentKeys.all
      // covers this report's threads and the dashboard batch cache alike.
      if (prev && result.comments_need_refresh && idle) {
        queryClient.invalidateQueries({ queryKey: commentKeys.all });
      }

      return result;
    },
  });
};

export const useUpdateAddresses = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-addresses", reportId],
    mutationFn: async (update: UpdateAddresses) => {
      return await updateAddresses(reportId, update);
    },
    onSuccess: (data) => {
      // Invalidate report list data because updating addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      // Premiums and summaries are derived from address data; refresh them
      // but keep the detail query, which we patch in place below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
      ]);

      const prev = queryClient.getQueryData<client.ReportResponse>(
        reportKeys.detail(reportId)
      );

      const updates = Object.fromEntries(
        data.addresses.map((a) => [a.address_id, a])
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(
          reportKeys.detail(reportId),
          {
            ...prev,
            report_json: {
              ...prev.report_json,
              sovs: prev.report_json.sovs.map((sov) => ({
                ...sov,
                addresses: sov.addresses.map((a) => updates[a.address_id] ?? a),
              })),
            },
          }
        );
      }
    },
  });
};

export const useAddManualAddress = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-manual-address", reportId],
    mutationFn: async (address: client.RawAddress) => {
      return await addManualAddress(reportId, address);
    },
    onSuccess: (data) => {
      // Invalidate report list data because adding addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      // Premiums and summaries are derived from address data; refresh them
      // but keep the detail query, which we replace wholesale below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
      ]);

      queryClient.setQueryData(reportKeys.detail(reportId), data.report);
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-report"],
    mutationFn: async (reportId: string) => {
      return await deleteReport(reportId);
    },
    onSuccess: (_data, reportId) => {
      // Drop the deleted report's cached detail (and everything derived from
      // it) and refresh the dashboard list so it no longer shows the removed
      // submission.
      queryClient.removeQueries({ queryKey: reportKeys.detail(reportId) });
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
    },
  });
};

export const useCalculatedPremium = (reportId: string, enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.calculatedPremium(reportId),
    queryFn: () => calculatePremium(reportId),
    refetchOnWindowFocus: false,
  });
};

// The CAT source the policy prices each included peril off — the configured
// ``peril_sources`` override, else the source the premium engine resolved.
// Lets banners scope to the active pricing scheme instead of every vendor.
export const useSelectedPerilSources = (
  specialtyPropertyInfo: SafeReportResponse
): PerilDataSource[] => {
  const { data: premiumData } = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    true
  );
  return useMemo(() => {
    const policyInfo = specialtyPropertyInfo.report_json.policy_info;
    const sources = new Set<PerilDataSource>();
    for (const peril of policyInfo?.included_perils ?? []) {
      const source =
        policyInfo?.peril_sources?.[peril] ??
        premiumData?.layers?.[0]?.peril_types[peril]?.resolved.source;
      if (source) sources.add(source);
    }
    return [...sources];
  }, [specialtyPropertyInfo.report_json.policy_info, premiumData]);
};

export const useLayerGateExposure = (
  reportId: string,
  layerId: string,
  enabled: boolean
) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.gateExposure(reportId, layerId),
    queryFn: () => getLayerGateExposure(reportId, layerId),
    refetchOnWindowFocus: false,
  });
};

export const useFinancialModelAudit = (
  reportId: string,
  source: FinancialModelAuditSource,
  enabled: boolean,
  batchId?: string | null
) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.financialModelAudit(reportId, source, batchId),
    queryFn: () => getFinancialModelAudit(reportId, source, batchId),
    refetchOnWindowFocus: false,
  });
};

export const useGateTrackingBase = (
  reportId: string,
  layerId: string,
  enabled: boolean
) => {
  return useQuery({
    enabled,
    queryKey: bookKeys.gateTrackingBase(reportId, layerId),
    queryFn: () => getGateTrackingBase(reportId, layerId),
    refetchOnWindowFocus: false,
  });
};

export const usePortfolioGateAccumulation = (enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: bookKeys.portfolioGateAccumulation(),
    queryFn: () => getPortfolioGateAccumulation(),
    refetchOnWindowFocus: false,
  });
};

export const useAalPremiumGatePortfolio = (enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: bookKeys.aalPremiumGatePortfolio(),
    queryFn: () => getAalPremiumGatePortfolio(),
    refetchOnWindowFocus: false,
  });
};

export const useAalPremiumGateTracking = (
  reportId: string,
  layerId: string,
  enabled: boolean
) => {
  return useQuery({
    enabled,
    queryKey: bookKeys.aalPremiumGateTracking(reportId, layerId),
    queryFn: () => getAalPremiumGateTracking(reportId, layerId),
    refetchOnWindowFocus: false,
  });
};

export const usePrecheck = (reportId: string) => {
  return useQuery({
    queryKey: reportKeys.precheck(reportId),
    queryFn: () => precheck(reportId),
    // Invalidated explicitly on edits, so a window-focus refetch adds nothing
    // but flips the CatModelSwitcher freshness icons to spinners.
    refetchOnWindowFocus: false,
  });
};

// Return type is intentionally "unknown" here so that callers don't use it
// directly, since it requires some interpretation. Instead, use useReport to
// read report data.
export const useUpdateReport = (
  reportId: string
): UseMutationResult<unknown, unknown, UpdateReport, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-report", reportId],
    mutationFn: async (update: UpdateReport) => {
      return await updateReport(reportId, update);
    },
    onSuccess: (updateResult) => {
      reconcileReportDetail(queryClient, reportId, updateResult, {
        invalidateDerived: true,
      });
    },
  });
};

/** Apply a sort-only reorder payload optimistically. Clones only the info
 *  subtree(s) being reordered — never the (potentially huge) SOV addresses — and
 *  shallow-spreads the rest of the report. Only sort_key on existing rows is
 *  touched; rows not present are skipped. */
const applyOrderPayload = (
  report: client.ReportResponse,
  update: UpdateReport
): client.ReportResponse => {
  const json = report.report_json;

  const limitInfo = update.limitOrder?.length
    ? cloneDeep(json.limit_info)
    : json.limit_info;
  for (const order of update.limitOrder ?? []) {
    const group =
      order.group === "manual"
        ? limitInfo?.manual_limits
        : order.filename != null
          ? limitInfo?.extracted_limits[order.filename]
          : undefined;
    const entry = group?.[order.peril_type];
    if (entry) {
      entry.excerpt_sort_keys = {
        ...entry.excerpt_sort_keys,
        ...order.excerpt_keys,
      };
    }
  }

  const deductibleInfo = update.deductibleOrder?.length
    ? cloneDeep(json.deductible_info)
    : json.deductible_info;
  for (const order of update.deductibleOrder ?? []) {
    const group =
      order.group === "manual"
        ? deductibleInfo?.manual_deductibles
        : order.filename != null
          ? deductibleInfo?.extracted_deductibles[order.filename]
          : undefined;
    const entry = group?.[order.peril_type];
    if (entry) {
      entry.excerpt_sort_keys = {
        ...entry.excerpt_sort_keys,
        ...order.excerpt_keys,
      };
    }
  }

  const sublimitInfo = update.sublimitOrder?.length
    ? cloneDeep(json.sublimit_info)
    : json.sublimit_info;
  for (const order of update.sublimitOrder ?? []) {
    const rows =
      order.group === "manual"
        ? sublimitInfo?.manual_sublimits
        : order.filename != null
          ? sublimitInfo?.extracted_sublimits[order.filename]
          : undefined;
    for (const entry of order.entries) {
      const row = rows?.find(
        (r) =>
          r.sublimit_name === entry.sublimit_name &&
          (r.classification ?? null) === (entry.classification ?? null)
      );
      if (row) row.sort_key = entry.sort_key;
    }
  }

  return {
    ...report,
    report_json: {
      ...json,
      limit_info: limitInfo,
      deductible_info: deductibleInfo,
      sublimit_info: sublimitInfo,
    },
  };
};

/**
 * Persist a sort-only reorder of limits / deductibles / sublimits.
 *
 * Unlike useUpdateReport this deliberately skips the broad list/derivative
 * invalidations: a reorder changes only display order, so nothing reruns and no
 * other view needs refreshing. The cached report is patched optimistically from
 * the same payload and then reconciled with the authoritative report.
 */
export const useReorderPolicyField = (
  reportId: string
): UseMutationResult<
  client.UpdateReportResponse,
  unknown,
  UpdateReport,
  { previous?: client.ReportResponse }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["reorder-policy-field", reportId],
    mutationFn: (update: UpdateReport) => updateReport(reportId, update),
    onMutate: async (update) => {
      const key = reportKeys.detail(reportId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<client.ReportResponse>(key);
      if (previous) {
        queryClient.setQueryData<client.ReportResponse>(
          key,
          applyOrderPayload(previous, update)
        );
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(reportKeys.detail(reportId), context.previous);
      }
    },
    onSuccess: (result) => {
      // Sort-only change — reconcile the report but skip the broad derived-view
      // invalidations (nothing else depends on display order).
      reconcileReportDetail(queryClient, reportId, result, {
        invalidateDerived: false,
      });
    },
  });
};

// Keyed under the report's detail prefix so the invalidation in
// useUpdateReport.onSuccess refetches the recipients automatically after an
// edit.
export const useClearanceRecipients = (reportId: string, enabled: boolean) => {
  return useQuery({
    enabled,
    queryKey: reportKeys.clearanceRecipients(reportId),
    queryFn: () => getClearanceEmailRecipients(reportId),
  });
};

export const useUsers = () => {
  return useQuery({
    queryKey: userKeys.all,
    queryFn: () => listUsers(),
    // Re-use the result for at least 5 seconds (just avoid re-fetching during load of the page, etc.)
    staleTime: 5000,
  });
};

export const useLocalUser = () => {
  return useUsers().data?.current_user;
};

export const useUserById = (
  userId: string | undefined | null
): client.User | undefined => {
  const users = useUsers();
  if (!userId) return undefined;
  return (users?.data?.all_company_users ?? []).find((u) => u.id === userId);
};

export const useCRMContacts = () => {
  return useQuery({
    queryKey: crmContactKeys.all,
    queryFn: () => listCRMContacts(),
    // Keep data fresh for 5 minutes before considering a refetch
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes even when unused
    gcTime: 10 * 60 * 1000,
    // Don't refetch when window regains focus
    refetchOnWindowFocus: false,
  });
};

/**
 * Merge a single contact into the cached `crm-contacts` list.
 * - Replaces an existing contact with the same id.
 * - Prepends the contact when it wasn't present (default behavior).
 * - Returns the new `ListContactsResponse` shape.
 */
function mergeContactIntoCache(
  queryClient: QueryClient,
  contact: client.ReportContact
): void {
  queryClient.setQueryData(
    crmContactKeys.all,
    (prev: client.ListContactsResponse | undefined) => {
      const existing = prev?.contacts ?? [];
      const replaced = existing.map((c) => (c.id === contact.id ? contact : c));
      const has = replaced.some((c) => c.id === contact.id);

      const contacts: client.ReportContact[] = has
        ? replaced
        : [contact, ...replaced];

      return { ...(prev ?? {}), contacts };
    }
  );
}
export const useCreateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-create-contact"],
    mutationFn: async (body: client.CreateContactRequest) => {
      return await createCRMContact(body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      }
    },
  });
};

export const useUpdateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-update-contact"],
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: client.UpdateContactRequest;
    }) => {
      return await updateCRMContact(id, body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: crmContactKeys.all });
      }
      // Reports embed contact data in both list rows and details, so refresh
      // everything report-related.
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
};

// The response carries both the file names and a name -> RFC 3339 upload time
// map (from GCS object metadata) for files uploaded through the app. Email
// attachments are absent from the map (they have no standalone GCS object).
export const useAdditionalFiles = (
  reportId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: reportKeys.additionalFiles(reportId),
    queryFn: () => listAdditionalFiles(reportId),
    enabled,
  });
};

export const useUploadAdditionalFiles = (
  reportId: string,
  onProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upload-additional-file", reportId],
    mutationFn: (files: File[]) =>
      uploadAdditionalFile(reportId, files, onProgress, isScreenshot),
    onSuccess: (response) => {
      // Refresh the report and its derived data; the files list itself is
      // patched from the response below.
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.additionalFiles(reportId),
      ]);
      queryClient.setQueryData(reportKeys.additionalFiles(reportId), response);
    },
  });
};

export const useDeleteAdditionalFile = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-additional-file", reportId],
    mutationFn: (names: string[]) => deleteAdditionalFile(reportId, names),
    onSuccess: (response) => {
      queryClient.setQueryData(reportKeys.additionalFiles(reportId), response);
    },
  });
};

export const useDeleteAttachment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-attachment", reportId],
    mutationFn: (filename: string) => deleteAttachment(reportId, filename),
    onSuccess: (attachmentInfo, filename) => {
      // The delete just removes the attachment_info entry; patch it into the
      // cached report so the attachment disappears without a full refetch.
      const prev = queryClient.getQueryData<client.ReportResponse>(
        reportKeys.detail(reportId)
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(
          reportKeys.detail(reportId),
          {
            ...prev,
            report_json: {
              ...prev.report_json,
              attachment_info: attachmentInfo,
            },
          }
        );
      }
      // The blob was moved out of additional_files/, so drop it from that cache
      // too; otherwise it would resurface as an unclassified file.
      queryClient.setQueryData<string[]>(
        reportKeys.additionalFiles(reportId),
        (files) => (files ?? []).filter((f) => f !== filename)
      );
      // Deleting an attachment reprocesses the components it impacted, so
      // surface the rerunning state, refresh the dashboard list, and refresh
      // this report's derived data — skipping the two entries just patched
      // above.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      invalidateReportDetail(queryClient, reportId, [
        reportKeys.detail(reportId),
        reportKeys.additionalFiles(reportId),
      ]);
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
    },
  });
};

export const useAddressesForMap = (lobs: LOB[]) => {
  return useQuery({
    queryKey: addressesForMapKeys.list(lobs),
    queryFn: () => loadAddressesForMap(lobs),
    refetchOnWindowFocus: false,
    staleTime: 3_600_000,
  });
};

export const useComments = (reportIds: string[]) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: commentKeys.batch(reportIds),
    queryFn: async () => {
      const data = await getBatchComments(reportIds);
      for (const [reportId, comments] of Object.entries(
        data.comments_by_report_id
      )) {
        queryClient.setQueryData(commentKeys.forReport(reportId), comments);
      }
      return data;
    },
    enabled: reportIds.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useCommentsForReport = (
  reportId: string,
  fetchEnabled: boolean = true
) => {
  return useQuery<client.Comments>({
    queryKey: commentKeys.forReport(reportId),
    queryFn: async () => {
      const data = await getBatchComments([reportId]);
      return data.comments_by_report_id[reportId] ?? { all_comments: [] };
    },
    enabled: fetchEnabled,
    refetchOnWindowFocus: false,
  });
};

export const useAddComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-comment", reportId],
    mutationFn: async (message: string) => {
      return await addComment(reportId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

export const useDeleteComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-comment", reportId],
    mutationFn: async (commentId: string) => {
      return await deleteComment(reportId, commentId);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

export const useEditComment = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["edit-comment", reportId],
    mutationFn: async ({
      commentId,
      message,
    }: {
      commentId: string;
      message: string;
    }) => {
      return await editComment(reportId, commentId, message);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(commentKeys.forReport(reportId), data.comments);
    },
  });
};

// A comment thread is either the report-level conversation, the thread for a
// single loss-run policy year, or the thread for a single attachment. Scope
// drives which comments are fetched/shown and what target the mutations write.
export type CommentScope =
  | { kind: "report" }
  | { kind: "loss_year"; policyYear: string }
  | { kind: "attachment"; attachmentName: string };

const targetTypeForScope = (
  scope: CommentScope
): client.CommentTargetType | null => {
  switch (scope.kind) {
    case "loss_year":
      return "loss_year";
    case "attachment":
      return "attachment";
    case "report":
      return null;
  }
};

// The target_key a mutation writes and the client-side filter keys on: the
// policy year for loss-run, the attachment name for attachments, none for
// report-level.
const targetKeyForScope = (scope: CommentScope): string | null => {
  switch (scope.kind) {
    case "loss_year":
      return scope.policyYear;
    case "attachment":
      return scope.attachmentName;
    case "report":
      return null;
  }
};

const commentsQueryKey = (
  reportId: string,
  targetType: client.CommentTargetType | null
) => {
  switch (targetType) {
    case "loss_year":
      return commentKeys.lossRunForReport(reportId);
    case "attachment":
      return commentKeys.attachmentForReport(reportId);
    default:
      return commentKeys.forReport(reportId);
  }
};

/**
 * The fetch + cache for one report's comments at a given scope. Sole owner of
 * the query key/fn so every consumer (the per-year threads and the referral
 * snapshot) shares one implementation and cache entry instead of duplicating —
 * and staying in sync with — a useQuery.
 */
const useReportCommentsQuery = (
  reportId: string,
  targetType: client.CommentTargetType | null,
  fetchEnabled: boolean
) =>
  useQuery<client.Comments>({
    queryKey: commentsQueryKey(reportId, targetType),
    queryFn: async () => {
      const data = await getBatchComments([reportId], targetType);
      return data.comments_by_report_id[reportId] ?? { all_comments: [] };
    },
    enabled: fetchEnabled,
    refetchOnWindowFocus: false,
  });

/**
 * Data + mutations for one comment thread, scoped to either the whole report or
 * a single loss-run policy year. Loss-run comments are fetched once per report
 * (all years) under a shared query key and filtered to the year here, so many
 * year threads on one screen share a single request and cache entry.
 */
export const useCommentThread = (
  reportId: string,
  scope: CommentScope,
  fetchEnabled: boolean = true
) => {
  const queryClient = useQueryClient();
  const targetType = targetTypeForScope(scope);
  const queryKey = commentsQueryKey(reportId, targetType);

  const commentsQuery = useReportCommentsQuery(
    reportId,
    targetType,
    fetchEnabled
  );

  const scopeComments = commentsQuery.data?.all_comments ?? [];
  // Loss-run and attachment threads share one fetch per report (all years / all
  // attachments) under a single query key, so filter to this scope's target_key
  // here. Report-level has no target_key and shows everything.
  const targetKey = targetKeyForScope(scope);
  const comments =
    targetKey === null
      ? scopeComments
      : scopeComments.filter((c) => c.target_key === targetKey);

  // The mutation responses return the comments for the whole scope (report-level
  // or all loss-run years), so the shared cache entry stays authoritative.
  const add = useMutation({
    mutationKey: ["add-comment", queryKey],
    mutationFn: async (message: string) =>
      addComment(reportId, message, targetType, targetKey),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data.comments),
  });

  const edit = useMutation({
    mutationKey: ["edit-comment", queryKey],
    mutationFn: async ({
      commentId,
      message,
    }: {
      commentId: string;
      message: string;
    }) => editComment(reportId, commentId, message, targetType),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data.comments),
  });

  const remove = useMutation({
    mutationKey: ["delete-comment", queryKey],
    mutationFn: async (commentId: string) =>
      deleteComment(reportId, commentId, targetType),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data.comments),
  });

  return { commentsQuery, comments, add, edit, remove };
};

/**
 * All loss-run comments for a report (every policy year), for read-only
 * consumers like the referral snapshot. Reuses the same fetch/cache as
 * useCommentThread's loss-run scope.
 */
export const useLossRunCommentsForReport = (reportId: string) =>
  useReportCommentsQuery(reportId, "loss_year", true);

export const useUpdatingReports = () => {
  const queryClient = useQueryClient();
  const doneUpdates = useRef<Set<string>>(new Set());
  return useQuery({
    queryKey: updatingReportKeys.all,
    queryFn: async (): Promise<client.ListUpdatingReportsResponse> => {
      const response = await getUpdatingReports(
        new Date(performance.timeOrigin)
      );

      // If any report finishes, invalidate everything cached for it — the
      // detail prefix covers the report itself plus all derived data
      // (premiums, summaries, integration data, files, threads).
      for (const update of response.updates) {
        if (update.outcome && !doneUpdates.current.has(update.update_id)) {
          doneUpdates.current.add(update.update_id);
          queryClient.invalidateQueries({
            queryKey: reportKeys.detail(update.report_id),
          });
        }
      }

      return response;
    },
    refetchInterval: 5_000,
    staleTime: 5_000,
  });
};

const INTEGRATION_TYPES_THAT_BLOCK_EDITS = ["RERUN_REPORT", "POLICY_FLY"];
export const useThisReportIsUpdating = (
  reportId: string,
  integrationType?: string
): { updating: client.ReportBeingUpdated } | undefined => {
  const updating = useUpdatingReports().data?.updates.find(
    (u) =>
      u.report_id === reportId &&
      !u.outcome &&
      INTEGRATION_TYPES_THAT_BLOCK_EDITS.includes(u.integration_type) &&
      (!integrationType || u.integration_type === integrationType)
  );
  return useMemo(() => updating && { updating }, [updating]);
};

export const useAllUpdatesForReport = (
  reportId: string,
  integrationType?: string
): client.ReportBeingUpdated[] => {
  const updates = useUpdatingReports().data?.updates ?? [];
  return useMemo(
    () =>
      updates.filter(
        (u) =>
          u.report_id === reportId &&
          !u.outcome &&
          (!integrationType || u.integration_type === integrationType)
      ),
    [updates, reportId, integrationType]
  );
};

export const useCancelJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["cancel-jobs"],
    mutationFn: async (updateIds: string[]) => {
      await Promise.all(
        updateIds.map((id) =>
          client.asgardCancelJob({
            requestBody: { report_integration_id: id },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
      queryClient.invalidateQueries({ queryKey: jobKeys.allQueueJobs });
    },
  });
};

// Bulk-enqueue Nearmap/HazardHub enrichment for many submissions at once
// (admin Enrichment tab). The enqueued jobs surface in the Jobs in Queue view,
// so invalidate that on success.
export const useBulkEnrich = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["bulk-enrich"],
    mutationFn: (vars: {
      reportIds: string[];
      sources: client.BulkEnrichmentRequest["sources"];
    }) => bulkEnrichSubmissions(vars.reportIds, vars.sources),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.allQueueJobs });
    },
  });
};

export const useEmailThreads = (reportId: string, enabled: boolean = true) => {
  return useQuery({
    enabled: enabled,
    queryKey: reportKeys.emailThreads(reportId),
    queryFn: () => fetchEmailThreads(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useUploadEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upload-email-thread", reportId],
    mutationFn: (files: File[]) => uploadEmailThread(reportId, files),
    onSuccess: () => {
      // The detail prefix covers the report, its email threads, and all
      // other derived data.
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
};

export const useExtractStagedAttachments = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["extract-staged-attachments", reportId],
    mutationFn: (selections: client.StagedAttachmentSelection[]) =>
      extractStagedAttachments(reportId, selections),
    onSuccess: () => {
      // Extraction adds attachments and may enqueue a partial rerun, so
      // refresh the report, its derived data, and the rerunning state.
      queryClient.invalidateQueries({ queryKey: reportKeys.lists() });
      invalidateReportDetail(queryClient, reportId, []);
      queryClient.invalidateQueries({ queryKey: updatingReportKeys.all });
    },
  });
};

export const useDeleteEmailThread = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-email-thread", reportId],
    mutationFn: (conversationId: string) =>
      deleteEmailThread(reportId, conversationId),
    onSuccess: () => {
      // The detail prefix covers the report, its email threads, and its
      // additional-files list.
      queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
};

export const useGeneratePolicyNumbers = (reportId: string) => {
  return useMutation({
    mutationKey: ["generate-policy-numbers", reportId],
    mutationFn: async () => {
      return await generatePolicyNumbers(reportId);
    },
  });
};

// Invalidate every cache that reflects a report's job state. Return
// this from a mutation's onSuccess to keep the mutation isPending
// until the refetches land.
const invalidateJobState = (
  queryClient: QueryClient,
  reportId: string
): Promise<unknown> =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: updatingReportKeys.all }),
    queryClient.invalidateQueries({ queryKey: jobKeys.reportJobs(reportId) }),
    queryClient.invalidateQueries({ queryKey: jobKeys.allQueueJobs }),
  ]);

export const useGeneratePolicyFlyApplication = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["generate-policy-fly-application", reportId],
    mutationFn: async (args: {
      perilsByLayer: Record<string, string>;
      additionalLayerId?: string;
    }) => {
      return await generatePolicyFlyApplication(
        reportId,
        args.perilsByLayer,
        args.additionalLayerId
      );
    },
    onSuccess: () => invalidateJobState(queryClient, reportId),
  });
};

export const useUpdatePolicyFlyApplication = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-policy-fly-application", reportId],
    mutationFn: async (args: {
      suffix: string;
      perilsInsuredDescription: string;
    }) => {
      return await updatePolicyFlyApplication(
        reportId,
        args.suffix,
        args.perilsInsuredDescription
      );
    },
    onSuccess: () => invalidateJobState(queryClient, reportId),
  });
};

export const useLinkPolicyFlyApplication = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["link-policy-fly-application", reportId],
    mutationFn: async (args: {
      layerId: string;
      suffix: string;
      url: string;
    }) => {
      return await linkPolicyFlyApplication(
        reportId,
        args.layerId,
        args.suffix,
        args.url
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: reportKeys.detail(reportId),
      }),
  });
};

export const useUnlinkPolicyFlyApplication = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["unlink-policy-fly-application", reportId],
    mutationFn: async (args: { suffix: string }) => {
      return await unlinkPolicyFlyApplication(reportId, args.suffix);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: reportKeys.detail(reportId),
      }),
  });
};

export const useEnqueueRater = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["enqueue-rater", reportId],
    mutationFn: async (templateId: string | undefined) => {
      return await enqueueRater(reportId, templateId);
    },
    onSuccess: () => invalidateJobState(queryClient, reportId),
  });
};

export const useEnqueueEqbdRater = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["enqueue-eqbd-rater", reportId],
    mutationFn: async () => {
      return await enqueueEqbdRater(reportId);
    },
    onSuccess: () => invalidateJobState(queryClient, reportId),
  });
};

// Per-hook bound on concurrent in-flight chunks. With three hooks
// (deductible, limit, integration-data) the global cap is ~3×this.
const MAX_CONCURRENT_CHUNKS = 2;

type AddressSummaryQueryResult<T> = {
  // `addresses` is a mutable map kept in a ref across renders — its
  // identity is stable, contents grow as chunks settle. Consumers depend
  // on `version` for change detection, not on the map identity.
  data: { addresses: { [addressId: string]: T }; version: number } | undefined;
  isLoading: boolean;
  hasError: boolean;
  loadedChunks: number;
  totalChunks: number;
};

const useChunkedAddressSummary = <T>(
  queryKeyPrefix: readonly unknown[],
  addressIds: string[],
  fetcher: (chunk: string[]) => Promise<{ addresses: { [k: string]: T } }>,
  chunkSize: number
): AddressSummaryQueryResult<T> => {
  const chunks = useMemo(() => chunk(addressIds, chunkSize), [addressIds]);

  const queries = useBoundedQueries<{ addresses: { [k: string]: T } }>(
    chunks.map((chunk) => ({
      queryKey: [...queryKeyPrefix, chunk[0]],
      queryFn: () => fetcher(chunk),
      refetchOnWindowFocus: false,
    })),
    MAX_CONCURRENT_CHUNKS,
    chunks
  );

  // Merged map is held in a ref and mutated in place across renders.
  // Total work to absorb N items across all chunks is O(N) — versus the
  // O(N²/chunk_size) cost of building a fresh merged object on every chunk
  // arrival. `seenChunkDataRef` tracks which result objects have already
  // been merged so the absorb effect is idempotent.
  const mergedRef = useRef<{ [k: string]: T }>({});
  const seenChunkDataRef = useRef<Set<object>>(new Set());
  const [version, setVersion] = useState(0);

  // Reset accumulator when the chunk list itself changes (different
  // addressIds → different report or enabled-SOV set). Sliding-window
  // reset is owned by useBoundedQueries.
  useEffect(() => {
    mergedRef.current = {};
    seenChunkDataRef.current = new Set();
    setVersion((v) => v + 1);
  }, [chunks]);

  // Absorb any chunks that have newly settled. Idempotent — chunks already
  // merged (tracked by their data reference) are skipped. Depends on a
  // content signature rather than `queries` itself, which would re-fire on
  // every parent render.
  const dataSignature = queries.map((q) => q.dataUpdatedAt ?? 0).join(",");
  useEffect(() => {
    let added = false;
    for (const q of queries) {
      if (q.data !== undefined && !seenChunkDataRef.current.has(q.data)) {
        Object.assign(mergedRef.current, q.data.addresses);
        seenChunkDataRef.current.add(q.data);
        added = true;
      }
    }
    if (added) setVersion((v) => v + 1);
    // queries excluded — dataSignature captures the content-relevant subset;
    // including it would invalidate on every render.
  }, [dataSignature]); // eslint-disable-line

  const settledCount = queries.filter(
    (q) => q.data !== undefined || q.isError
  ).length;
  const succeededCount = queries.filter((q) => q.data !== undefined).length;
  const hasError = queries.some((q) => q.isError);

  return useMemo(
    () => ({
      data:
        chunks.length === 0 || succeededCount > 0
          ? { addresses: mergedRef.current, version }
          : undefined,
      isLoading: chunks.length > 0 && settledCount < chunks.length,
      hasError,
      loadedChunks: succeededCount,
      totalChunks: chunks.length,
    }),
    [version, succeededCount, settledCount, hasError, chunks.length]
  );
};

export const useDeductibleSummary = (reportId: string, addressIds: string[]) =>
  useChunkedAddressSummary<{ [perilCode: string]: client.DeductibleSummary }>(
    reportKeys.deductibleSummary(reportId),
    addressIds,
    (chunk) => getDeductibleSummary(reportId, chunk),
    client.$DeductibleSummaryRequest.properties.address_ids.maxItems
  );

export const useLimitSummary = (reportId: string, addressIds: string[]) =>
  useChunkedAddressSummary<{ [perilCode: string]: client.LimitSummary }>(
    reportKeys.limitSummary(reportId),
    addressIds,
    (chunk) => getLimitSummary(reportId, chunk),
    client.$LimitSummaryRequest.properties.address_ids.maxItems
  );

export const useIntegrationData = (
  reportId: string,
  types: client.IntegrationDataRequest["types"],
  addressIds: string[]
) =>
  useChunkedAddressSummary<client.AddressIntegrationData>(
    reportKeys.integrationData(reportId, types),
    addressIds,
    (chunk) => getIntegrationData(reportId, types, chunk),
    client.$IntegrationDataRequest.properties.address_ids.maxItems
  );

export const useGoogleGroupStats = (email: string | null) =>
  useQuery({
    enabled: !!email,
    queryKey: googleGroupStatsKeys.forEmail(email ?? ""),
    queryFn: () => getGoogleGroupStats(email!),
    refetchOnWindowFocus: false,
  });

export const locationClassificationKey = (
  state: string | null | undefined,
  county: string | null | undefined
): LocationClassificationKey =>
  `${state ?? ""}|${county ?? ""}` as LocationClassificationKey;

// Catastrophe gate classification (wind tier, wind gate, EQ gate) for the SOV
// pivot panel. Takes addresses, derives the unique (state, county) pairs, and
// requests classifications for just those — request size scales with unique
// counties, not address count.
export const useLocationClassifications = (
  addresses: {
    state: string | null | undefined;
    county: string | null | undefined;
  }[]
) => {
  // Dedupe into a list of unique LocationKey payloads, sorted so the
  // react-query cacheKey is stable across re-orderings of the input.
  const locations: LocationKey[] = useMemo(() => {
    const seen = new Set<LocationClassificationKey>();
    const out: LocationKey[] = [];
    for (const a of addresses) {
      const key = locationClassificationKey(a.state, a.county);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ state: a.state ?? null, county: a.county ?? null });
    }
    out.sort((a, b) =>
      locationClassificationKey(a.state, a.county).localeCompare(
        locationClassificationKey(b.state, b.county)
      )
    );
    return out;
  }, [addresses]);

  const cacheKey = useMemo(
    () =>
      locations
        .map((l) => locationClassificationKey(l.state, l.county))
        .join(","),
    [locations]
  );

  return useQuery({
    enabled: locations.length > 0,
    queryKey: windTierKeys.forLocations(cacheKey),
    queryFn: async () => {
      const response = await getLocationClassifications(locations);
      const map = new Map<LocationClassificationKey, LocationClassification>();
      for (const r of response.results) {
        map.set(locationClassificationKey(r.state, r.county), {
          windTier: r.wind_tier,
          windGate: r.wind_gate,
          eqGate: r.eq_gate,
        });
      }
      return map;
    },
    refetchOnWindowFocus: false,
  });
};
